import React, { useEffect, useState } from "react";
import { Edit, Save, X, Plus, RotateCw } from "lucide-react";
import AdminCardActions from "../components/AdminCardActions";
import ToolbarSearch from "../components/ToolbarSearch";
import {
    fetchModes,
    fetchNetworks,
    updateMode,
    createMode
} from "../services/work_modes";

const initialMode = {
    work_mode_name: "",
    price: 0.00,
    descriptions: "",
    status: true,
    network_name: "",
};

const WorkModesPageAdmins = () => {
    const [modes, setModes] = useState([]);
    const [filteredModes, setFilteredModes] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortBy, setSortBy] = useState("work_mode_name");
    const [sortDir, setSortDir] = useState("asc");
    const [networks, setNetworks] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [editedData, setEditedData] = useState(initialMode);
    const [originalData, setOriginalData] = useState(initialMode);
    const [newMode, setNewMode] = useState(initialMode);
    const [showAddModal, setShowAddModal] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        loadModes();
        loadNetworks();

        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const q = searchQuery.toLowerCase();
        const filtered = modes.filter((m) =>
            (m.work_mode_name || "").toString().toLowerCase().includes(q) ||
            (m.descriptions || "").toString().toLowerCase().includes(q) ||
            (m.network_name || "").toString().toLowerCase().includes(q)
        );
        setFilteredModes(filtered);
    }, [modes, searchQuery]);

    const loadModes = async () => {
        const data = await fetchModes();
        setModes(data);
    };

    const loadNetworks = async () => {
        const data = await fetchNetworks();
        setNetworks(data);
    };

    const handleFieldChange = (setter) => (e) => {
        const { name, value, type } = e.target;
        const parsedValue = type === "number"
            ? parseFloat(value)
            : value === "true"
                ? true
                : value === "false"
                    ? false
                    : value;

        setter((prev) => ({ ...prev, [name]: parsedValue }));
    };

    const handleEdit = (item) => {
        setEditingId(item.id);
        setEditedData(item);
        setOriginalData(item); // зберігаємо оригінальні дані
    };

    const handleCancelEdit = () => {
        setEditedData(originalData);
        setEditingId(null);
    };

    const handleSave = async (id) => {
        await updateMode(id, editedData);
        setEditingId(null);
        loadModes();
    };

    const handleAdd = async () => {
        if (!newMode.work_mode_name.trim() || !newMode.price) {
            alert("Назва та ціна не можуть бути порожніми!");
            return;
        }
        await createMode(newMode);
        setNewMode(initialMode);
        setShowAddModal(false);
        loadModes();
    };

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("asc");
        }
    };

    const getSortedData = () => {
        if (!filteredModes) return [];
        const sorted = [...filteredModes];
        sorted.sort((a, b) => {
            let valA = a[sortBy] || "";
            let valB = b[sortBy] || "";
            valA = String(valA).toLowerCase();
            valB = String(valB).toLowerCase();
            if (sortDir === "asc") {
                return valA > valB ? 1 : valA < valB ? -1 : 0;
            } else {
                return valA < valB ? 1 : valA > valB ? -1 : 0;
            }
        });
        return sorted;
    };

    const statusOptions = [
        { value: true, label: "Активна" },
        { value: false, label: "Неактивна" }
    ];

    return (
        <div>
            <div className="config-header">
                <div className="header-buttons">
                    <button className="add-btn toolbar-icon-btn" onClick={() => setShowAddModal(true)} title="Додати" aria-label="Додати">
                        <Plus size={16} />
                    </button>
                    <button className="add-btn refresh toolbar-icon-btn" onClick={loadModes} title="Оновити" aria-label="Оновити">
                        <RotateCw size={16} />
                    </button>
                    <ToolbarSearch
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder="Пошук режимів/опису/мережі..."
                    />
                </div>
            </div>

            {showAddModal && (
                <div className="modal-backdrop" onClick={() => setShowAddModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Додати режим</h3>
                        <input
                            name="work_mode_name"
                            placeholder="Назва"
                            value={newMode.work_mode_name}
                            onChange={handleFieldChange(setNewMode)}
                        />
                        <input
                            name="price"
                            type="number"
                            step="0.01"
                            placeholder="Ціна"
                            value={newMode.price}
                            onChange={handleFieldChange(setNewMode)}
                        />
                        <input
                            name="descriptions"
                            placeholder="Опис"
                            value={newMode.descriptions}
                            onChange={handleFieldChange(setNewMode)}
                        />
                        <select
                            name="network_name"
                            value={newMode.network_name}
                            onChange={handleFieldChange(setNewMode)}
                        >
                            <option value="">Оберіть мережу</option>
                            {networks.filter(n => n.status).map(n => (
                                <option key={n.id} value={n.network_name}>{n.network_name}</option>
                            ))}
                        </select>
                        <select
                            name="status"
                            value={newMode.status}
                            onChange={handleFieldChange(setNewMode)}
                        >
                            {statusOptions.map(opt => (
                                <option key={opt.value.toString()} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                        <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                            <button className="add-btn" onClick={handleAdd}>Додати</button>
                            <button onClick={() => setShowAddModal(false)} className="add-btn cancel-btn">Скасувати</button>
                        </div>
                    </div>
                </div>
            )}

            {isMobile ? (
                <div className="config-grid">
                    {filteredModes.map((mode) => (
                        <div key={mode.id} className="config-card">
                            <div><strong>Назва:</strong> {editingId === mode.id ? (
                                <input
                                    name="work_mode_name"
                                    value={editedData.work_mode_name}
                                    onChange={handleFieldChange(setEditedData)}
                                />
                            ) : (
                                <span>{mode.work_mode_name}</span>
                            )}</div>

                            <div><strong>Ціна:</strong> {editingId === mode.id ? (
                                <input
                                    name="price"
                                    type="number"
                                    step="0.01"
                                    value={editedData.price}
                                    onChange={handleFieldChange(setEditedData)}
                                />
                            ) : (
                                <span>{mode.price}</span>
                            )}</div>

                            <div><strong>Опис:</strong> {editingId === mode.id ? (
                                <input
                                    name="descriptions"
                                    value={editedData.descriptions}
                                    onChange={handleFieldChange(setEditedData)}
                                />
                            ) : (
                                <span>{mode.descriptions}</span>
                            )}</div>

                            <div><strong>Статус:</strong> {editingId === mode.id ? (
                                <select
                                    name="status"
                                    value={editedData.status}
                                    onChange={handleFieldChange(setEditedData)}
                                >
                                    {statusOptions.map(opt => (
                                        <option key={opt.value.toString()} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <span>{mode.status ? "Активна" : "Неактивна"}</span>
                            )}</div>

                            <div><strong>Мережа:</strong> {editingId === mode.id ? (
                                <select
                                    name="network_name"
                                    value={editedData.network_name}
                                    onChange={handleFieldChange(setEditedData)}
                                >
                                    <option value="">(Не прив’язано)</option>
                                    {networks.filter(n => n.status).map(n => (
                                        <option key={n.id} value={n.network_name}>
                                            {n.network_name}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <span>{mode.network_name || "(Не прив’язано)"}</span>
                            )}</div>

                            <AdminCardActions
                                editing={editingId === mode.id}
                                onEdit={() => handleEdit(mode)}
                                onSave={() => handleSave(mode.id)}
                                onCancel={handleCancelEdit}
                            />
                        </div>
                    ))}
                </div>
            ) : (
                <div className="table-responsive">
                    <table>
                        <thead>
                            <tr>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("work_mode_name")}>
                                    Назва {sortBy === "work_mode_name" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("price")}>
                                    Ціна {sortBy === "price" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("descriptions")}>
                                    Опис {sortBy === "descriptions" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("status")}>
                                    Статус {sortBy === "status" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("network_name")}>
                                    Мережа {sortBy === "network_name" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th>Дії</th>
                            </tr>
                        </thead>
                        <tbody>
                            {getSortedData().map((mode) => (
                                <tr key={mode.id}>
                                    <td>
                                        {editingId === mode.id ? (
                                            <input
                                                name="work_mode_name"
                                                value={editedData.work_mode_name}
                                                onChange={handleFieldChange(setEditedData)}
                                            />
                                        ) : mode.work_mode_name}
                                    </td>
                                    <td>
                                        {editingId === mode.id ? (
                                            <input
                                                name="price"
                                                type="number"
                                                step="0.01"
                                                value={editedData.price}
                                                onChange={handleFieldChange(setEditedData)}
                                            />
                                        ) : mode.price}
                                    </td>
                                    <td>
                                        {editingId === mode.id ? (
                                            <input
                                                name="descriptions"
                                                value={editedData.descriptions}
                                                onChange={handleFieldChange(setEditedData)}
                                            />
                                        ) : mode.descriptions}
                                    </td>
                                    <td>
                                        {editingId === mode.id ? (
                                            <select
                                                name="status"
                                                value={editedData.status}
                                                onChange={handleFieldChange(setEditedData)}
                                            >
                                                {statusOptions.map(opt => (
                                                    <option key={opt.value.toString()} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </select>
                                        ) : mode.status ? "Активна" : "Неактивна"}
                                    </td>
                                    <td>
                                        {editingId === mode.id ? (
                                            <select
                                                name="network_name"
                                                value={editedData.network_name}
                                                onChange={handleFieldChange(setEditedData)}
                                            >
                                                <option value="">(Не прив’язано)</option>
                                                {networks.filter(n => n.status).map(n => (
                                                    <option key={n.id} value={n.network_name}>{n.network_name}</option>
                                                ))}
                                            </select>
                                        ) : mode.network_name || "(Не прив’язано)"}
                                    </td>
                                    <td className="actions">
                                        {editingId === mode.id ? (
                                            <>
                                                <button
                                                    className="icon-button"
                                                    onClick={() => handleSave(mode.id)}
                                                    title="Зберегти"
                                                >
                                                    <Save className="icon-btn" />
                                                </button>
                                                <button
                                                    className="icon-button"
                                                    onClick={handleCancelEdit}
                                                    title="Скасувати"
                                                >
                                                    <X className="icon-btn" />
                                                </button>
                                            </>
                                        ) : (
                                            <button
                                                className="icon-button"
                                                onClick={() => handleEdit(mode)}
                                                title="Редагувати"
                                            >
                                                <Edit className="icon-btn" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default WorkModesPageAdmins;
